import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { useSetProjectStatus } from "@/features/finances/hooks/useFinances";
import type { Project } from "@/features/enterprises/types";

interface Props {
  project: Pick<Project, "id" | "status" | "suspension_fraction">;
  /** Dezaktywacja (inactive) ma ostrzejszą bramkę niż wstrzymanie: CEO lub dyrektor
   *  techniczny z dostępem finansowym (canDeactivateProject). Koordynator jej nie ma. */
  canDeactivate: boolean;
}

function errorMessage(err: unknown): string {
  const detail = (err as { response?: { data?: { errors?: { detail?: unknown } } } })
    ?.response?.data?.errors?.detail;
  return typeof detail === "string" ? detail : "Nie udało się zmienić statusu przedsięwzięcia.";
}

/**
 * Przyciski zmiany statusu przedsięwzięcia (Aktywuj / Wstrzymaj / Dezaktywuj) wraz
 * z suwakiem procentu wstrzymania. Renderuj TYLKO dla osób z prawem do zmiany statusu
 * (canSetProjectStatus) — bramka jest u rodzica, a backend i tak odmówi (403) innym.
 * „Dezaktywuj" pokazuje się dodatkowo tylko przy canDeactivate (koordynator wstrzymuje,
 * ale nie zamyka projektu).
 * Wspólne dla karty finansów i pulpitu przedsięwzięcia: koordynator bez dostępu do
 * kwot nie ma wstępu na finanse, więc status swojego projektu zmienia z pulpitu.
 */
export function ProjectStatusActions({ project, canDeactivate }: Props) {
  const setStatus = useSetProjectStatus(project.id);
  const [showSuspendForm, setShowSuspendForm] = useState(false);
  const [suspendFraction, setSuspendFraction] = useState(
    parseFloat(project.suspension_fraction) > 0
      ? Math.round(parseFloat(project.suspension_fraction) * 100)
      : 100,
  );

  const isActive = project.status === "active";
  const isSuspended = project.status === "suspended";
  const isInactive = project.status === "inactive";

  const handleActivate = () => setStatus.mutate({ status: "active" });
  const handleDeactivate = () => {
    if (
      !confirm(
        "Dezaktywować przedsięwzięcie? Automatyczny (proporcjonalny) przydział kosztów płac zostanie zatrzymany — bezpośrednie przydziały i koszty przypisane liczą się nadal.",
      )
    )
      return;
    setStatus.mutate({ status: "inactive" });
  };
  const handleSuspend = () => {
    setStatus.mutate(
      { status: "suspended", suspension_fraction: suspendFraction / 100 },
      { onSuccess: () => setShowSuspendForm(false) },
    );
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        {isSuspended && (
          <span className="text-xs text-yellow-700 bg-yellow-100 px-2 py-0.5 rounded">
            {Math.round(parseFloat(project.suspension_fraction) * 100)}% wstrzymane
          </span>
        )}
        {(isSuspended || isInactive) && (
          <Button size="sm" variant="secondary" loading={setStatus.isPending} onClick={handleActivate}>
            Aktywuj
          </Button>
        )}
        {(isActive || isSuspended) && (
          <Button size="sm" variant="secondary" onClick={() => setShowSuspendForm(!showSuspendForm)}>
            {isSuspended ? "Zmień wstrzymanie" : "Wstrzymaj"}
          </Button>
        )}
        {!isInactive && canDeactivate && (
          <Button size="sm" variant="secondary" loading={setStatus.isPending} onClick={handleDeactivate}>
            Dezaktywuj
          </Button>
        )}
      </div>

      {setStatus.isError && (
        <p className="text-xs text-red-600 mt-1">{errorMessage(setStatus.error)}</p>
      )}

      {showSuspendForm && (
        <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-2">
            Procent wstrzymania: <strong>{suspendFraction}%</strong>
          </label>
          <input
            type="range"
            min={10}
            max={100}
            step={5}
            value={suspendFraction}
            onChange={(e) => setSuspendFraction(parseInt(e.target.value))}
            className="w-full accent-yellow-500"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Wstrzymanie redukuje wyłącznie automatycznie (proporcjonalnie) rozdzielane koszty
            płac i koszty nieprzypisane — zwolniona część przechodzi na pozostałe przedsięwzięcia.
            Bezpośrednie przydziały pracowników i koszty przypisane do tego przedsięwzięcia
            liczą się nadal w 100%.
          </p>
          <div className="flex gap-2 mt-3">
            <Button size="sm" loading={setStatus.isPending} onClick={handleSuspend}>
              Zastosuj wstrzymanie
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setShowSuspendForm(false)}>
              Anuluj
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
